import React, { useState, useEffect, useRef, useContext } from 'react'
import { parse } from 'papaparse'
import { Link, useLocation } from 'react-router-dom'

import { errorHandlerService } from '../../common/services/error-handler.service'
import { FullPageLoader } from '../../common/components/full-page-loader'
import { Loader } from '../../common/components/loader'
import { useCommentContext } from '../contexts/comments.context'
import { ReportPageContextProvider } from '../contexts/report-page.context'
import { ReportBlock } from '../blocks/report-block'
import { reportService } from '../report.service'
import { reportCacheService } from '../report-cache.service'
import { RouterContext } from '../../common/contexts/routerContext'
import { REPORT_VIEWER_MODE } from '../constants'
import { PageControlBar } from '../blocks/page-control-bar'
import { FloatingBackLink } from '../../common/components/floating-back-link'
import { TocMenu } from '../toc-menu'
import { ReportPageLayout } from './report-page-layout'
import { reportPageService } from '../report-page.service'
import { UserContext } from '../../common/contexts/userContext'

import { useStyles } from './report-page.style'
import { BlockActions } from '../blocks/block-actions'

const DATE_FORMAT = 'MMMM D, YYYY'

export const ReportPage = function ({ report, mode, highlightBlockId }) {
  const classes = useStyles()
  const [layout, setLayout] = useState()
  const [dataFiles, setDataFiles] = useState()
  const contentRef = useRef()
  const routerContext = useContext(RouterContext)
  const parentVersion = routerContext.getQueryParam('parentVersion')

  const user = useContext(UserContext)
  const hasCommentAccess = reportService.hasCommentAccess(report) && user.hasPermission('feature:report-comments')
  const {
    openBlockComments,
    openPagePropertyComments
  } = useCommentContext() || {}

  const location = useLocation()
  useEffect(() => {
    if (location && location.hash && contentRef.current) {
      // I am doing setTimeout to aid with rendering as much of the page as possible before scrolling down.
      // For example, there could be a table and that table renders data after this componet has already rendered.
      // Unfortunately, there's not a good way to detect whether or not all components have finished fetching data.
      setTimeout(() => {
        // BLU-582 - Sometimes contentRef.current can be null because this code is inside setTimeout, making it asynchronous.
        // When the component re-renders, contentRef.current can change before code inside setTimeout is called.
        if (!contentRef.current) {
          return
        }

        const blockId = location.hash.substring(1)
        const block = contentRef.current.querySelector(`[data-block-key="${blockId}"]`)
        if (block) {
          block.scrollIntoView({ behavior: 'smooth' })
        }
      }, 500)
    }
  })

  useEffect(() => {
    loadReportData()
  }, [])

  useEffect(() => {
    // Find the block and highlight it if there is a block id to highlight
    if (layout && highlightBlockId) {
      const block = contentRef.current.querySelector(`[data-block-key="${highlightBlockId}"]`)
      if (block) {
        block.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }, [ highlightBlockId ])

  async function loadReportData () {
    try {
      const dataContainerId = (report.parent.dataSets || {}).containerId || report.parent.dataContainerId || report.containerId
      const layout = report.layout
      const dataFiles = await Promise.all((layout.dataFiles || []).map(async ({ key, name }) => {
        const value = await getContainerFile(dataContainerId, name)
        return { key, value }
      }))
      setDataFiles(dataFiles)
      layout.blocks = reportPageService.moveChildBlocksToParent(layout.blocks)
      setLayout(layout)
    } catch (error) {
      errorHandlerService.handleServerError(error)
    }
  }

  /**
   * Get layout blocks, filtering out any title blocks,
   * which is a legacy case of reports having title blocks
   * saved to the database.
   */
  function getBlocks () {
    if (layout && layout.blocks) {
      return layout.blocks.filter((block) => block.key !== 'title')
    } else {
      return []
    }
  }

  if (!layout) {
    if (mode === REPORT_VIEWER_MODE.READ) {
      return <FullPageLoader />
    } else {
      return (
        <div className="page-content-center">
          <Loader />
        </div>
      )
    }
  }

  return (
    <div ref={contentRef} className={classes.pageLayoutWrapper}>
      <ReportPageLayout mode={mode} hasPageControlBar={layout.pageControlBar !== undefined}>
        <ReportPageContextProvider page={report}>
          <div className={classes.contentHeader}>
            <div className={classes.lastUpdate}>Updated: {report.publishedOrUpdated ? report.publishedOrUpdated.format(DATE_FORMAT) : ''}</div>
            <BlockActions
              actions={{
                comment: {
                  onClick () {
                    openPagePropertyComments('title')
                  }
                }
              }}
              enabled={hasCommentAccess}
              data-code="page-title"
              data-qa="page-title-actions"
            >
              <h1 className={classes.title} data-qa="page-title">{report.title}</h1>
            </BlockActions>
            {report.description && (
              <BlockActions
                actions={{
                  comment: {
                    onClick () {
                      openPagePropertyComments('description')
                    }
                  }
                }}
                enabled={hasCommentAccess}
                data-code="page-description"
                data-qa="page-description-actions"
              >
                <p className={classes.description} data-qa="page-description">{report.description}</p>
              </BlockActions>
            )}
            <div className={classes.metadata}>
              {report.firstPublishedDate &&
                <div>First published: {report.firstPublishedDate.format(DATE_FORMAT)}</div>
              }
              <div>Filed under: <Link to={routerContext.getReportListRoute(report)}>{report.parent.title}</Link></div>
            </div>
          </div>
          { getBlocks().map(({ key, type, dataKey, title, description, props, caption, methods }) => (
            <div
              key={key}
              data-block-key={key}
              className={(highlightBlockId === key) ? classes.blockHighlight : ''}
            >
              <BlockActions
                actions={{
                  comment: {
                    onClick () {
                      openBlockComments(key)
                    }
                  }
                }}
                enabled={hasCommentAccess}
                data-code={key}
                data-qa={`editable-${type}-block`}
              >
                <ReportBlock
                  blockKey={key}
                  type={type}
                  dataKey={dataKey}
                  title={title}
                  description={description}
                  props={props}
                  dataFiles={dataFiles}
                  report={report}
                  caption={caption}
                  methods={methods}
                />
              </BlockActions>
            </div>
          ))
          }
          {report.actions && (report.actions.prev || report.actions.next) && (
            <div className={classes.pageNavigator} data-qa="page-navigator">
              <div>
                {report.actions.prev && (
                  <Link to={reportService.getPaginationLink({ report, parentVersion, type: 'prev' })} className={classes.prevPageContainer}>
                    <div className={classes.prevPageChevron}></div>
                    <div className="flex-auto">
                      <div className={classes.pageNavLabel}>Previous Page</div>
                      <div className={classes.pageNavDesc}>{report.actions.prev.title}</div>
                    </div>
                  </Link>
                )}
              </div>
              <div className="flex-auto"></div>
              <div>
                {report.actions.next && (
                  <Link to={reportService.getPaginationLink({ report, parentVersion, type: 'next' })} className={classes.nextPageContainer}>
                    <div className="flex-auto">
                      <div className={classes.pageNavLabel}>Next Page</div>
                      <div className={classes.pageNavDesc}>{report.actions.next.title}</div>
                    </div>
                    <div className={classes.nextPageChevron}></div>
                  </Link>
                )}
              </div>
            </div>
          )}
          <TocMenu report={report} mode={mode} />
          { layout.pageControlBar && <div className={classes.pageControlBarContainer}><PageControlBar {...layout.pageControlBar} /></div> }
          {mode === REPORT_VIEWER_MODE.READ &&
            <FloatingBackLink to={routerContext.getReportListRoute(report)} />
          }
        </ReportPageContextProvider>
      </ReportPageLayout>
    </div>
  )
}

async function getContainerFile (containerId, fileName) {
  let { data } = await reportCacheService.getContainerFile(containerId, fileName)
  if (fileName.endsWith('.csv')) {
    const result = parse(data, { header: true, dynamicTyping: true, skipEmptyLines: true })
    data = result.data
  }
  return data
}
